import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Dialog, DialogBody, DialogSurface, Select, Spinner, Switch } from "@fluentui/react-components";
import {
  CalendarLtr20Regular,
  CalendarMonth20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Clock20Regular,
  CopyRegular,
  Dismiss20Regular,
  Document20Regular,
  EmojiSmileSlight20Regular,
  Location20Regular,
  People20Regular,
  Print20Regular,
  Save20Regular,
  ArrowRepeatAll20Regular,
  Video20Regular,
  TextBulletListLtr20Regular,
} from "@fluentui/react-icons";
import styles from "./NewEventDialog.module.css";
import type { Guest, Repeat } from "../../../../spurt/calendarApi";

/* ------------------------------------------------------------------ *
 *  New event (Teams style): title, people, date and time, location,
 *  Spurt meeting switch, description, agenda, and a day panel that shows
 *  what else is on that day so a clash is visible before you save.
 *  The parent owns the form, so it works in both calendar screens.
 * ------------------------------------------------------------------ */

export interface NewEventForm {
  /** null = a new event */
  id: string | null;
  title: string;
  calId: string;
  date: string; // yyyy-mm-dd
  start: string; // HH:mm
  end: string; // HH:mm
  repeat: Repeat;
  /** the repeat the event had when the form opened (an unchanged repeat is not sent again) */
  was: Repeat;
  location: string;
  /** a Spurt meeting with a join link */
  meeting: boolean;
  /** the join link when the event already has one */
  meetingUrl: string;
  people: Guest[];
  notes: string;
  agenda: string;
}

export interface DayBusy {
  start: number;
  end: number;
  title: string;
}

export interface CalChoice {
  id: string;
  name: string;
  color: string;
}

export interface NewEventDialogProps {
  form: NewEventForm | null;
  calendars: CalChoice[];
  error: string;
  saving: boolean;
  /** what else is on a day (yyyy-mm-dd), for the day panel */
  busyFor: (date: string) => DayBusy[];
  /** people the invite box can suggest */
  directory: Guest[];
  onChange: (patch: Partial<NewEventForm>) => void;
  onSave: () => void;
  onCancel: () => void;
  /** called when the person picks another day in the panel */
  onToast?: ((msg: string) => void) | undefined;
}

const ROW = 52;
const pad2 = (n: number): string => (n < 10 ? "0" + n : String(n));
const toMin = (t: string): number => {
  const p = t.split(":");
  return Number(p[0]) * 60 + Number(p[1] || 0);
};
const hhmm = (m: number): string => `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
const addDay = (ymd: string, n: number): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  const x = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + n);
  return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`;
};
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const longDay = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  const x = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  return `${DOW[x.getDay()]}, ${x.getDate()} ${MON[x.getMonth()]}, ${x.getFullYear()}`;
};
const EMOJI = ["😀", "😊", "👍", "🙏", "🎉", "📅", "✅", "🔥", "💡", "❤️", "🤝", "📎"];

const same = (a: Guest, b: Guest): boolean => (a.email && b.email ? a.email.toLowerCase() === b.email.toLowerCase() : a.userId && b.userId ? a.userId === b.userId : a.name.toLowerCase() === b.name.toLowerCase());
const label = (g: Guest): string => g.name || g.email;

const NewEventDialog: React.FC<NewEventDialogProps> = ({ form, calendars, error, saving, busyFor, directory, onChange, onSave, onCancel, onToast }) => {
  const [typed, setTyped] = useState("");
  const [sugOpen, setSugOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [series, setSeries] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const id = form?.id ?? "new";

  useEffect(() => {
    setTyped("");
    setSugOpen(false);
    setEmojiOpen(false);
    setSeries(!!form && form.repeat !== "none");
  }, [id, form === null]); // eslint-disable-line react-hooks/exhaustive-deps

  // open the day panel around the event's start
  useEffect(() => {
    if (form && scrollRef.current) scrollRef.current.scrollTop = Math.max(0, (toMin(form.start) / 60 - 1) * ROW);
  }, [id, form === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => {
    if (!form) return [];
    const q = typed.trim().toLowerCase();
    return directory
      .filter((g) => !form.people.some((p) => same(p, g)))
      .filter((g) => !q || label(g).toLowerCase().includes(q) || g.email.toLowerCase().includes(q))
      .slice(0, 6);
  }, [directory, form, typed]);

  if (!form) {
    return (
      <Dialog open={false}>
        <DialogSurface />
      </Dialog>
    );
  }

  const busy = busyFor(form.date).filter((b) => b.end > 0);
  const s = toMin(form.start);
  const e = Math.max(toMin(form.end), s + 15);
  const clash = busy.some((b) => b.start < e && b.end > s);

  const addPerson = (g: Guest) => {
    if (form.people.some((p) => same(p, g))) return;
    onChange({ people: [...form.people, g] });
    setTyped("");
  };
  /** Free text becomes a person: an e-mail address, a name from the directory, or a name that is only listed. */
  const commitTyped = () => {
    const t = typed.trim().replace(/[;,]+$/, "").trim();
    if (!t) return;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) {
      const hit = directory.find((g) => g.email.toLowerCase() === t.toLowerCase());
      addPerson(hit ?? { name: "", email: t, userId: null });
      return;
    }
    const hit = directory.find((g) => label(g).toLowerCase() === t.toLowerCase());
    addPerson(hit ?? { name: t, email: "", userId: null });
  };

  const setSlot = (startMin: number) => {
    const len = Math.max(toMin(form.end) - toMin(form.start), 15);
    const st = Math.min(Math.max(0, Math.round(startMin / 15) * 15), 24 * 60 - 15);
    onChange({ start: hhmm(st), end: hhmm(Math.min(st + len, 24 * 60 - 1)) });
  };

  const insertEmoji = (em: string) => {
    const el = notesRef.current;
    const at = el ? el.selectionStart : form.notes.length;
    onChange({ notes: form.notes.slice(0, at) + em + form.notes.slice(at) });
    setEmojiOpen(false);
    window.setTimeout(() => el?.focus(), 0);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(form.meetingUrl);
      onToast?.("Meeting link copied");
    } catch {
      onToast?.("Couldn’t copy the link");
    }
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) {
      onToast?.("Allow pop-ups to print");
      return;
    }
    const esc = (x: string) => x.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
    w.document.write(
      `<!doctype html><title>${esc(form.title || "Event")}</title><style>body{font:14px Segoe UI,Arial,sans-serif;margin:24px;color:#242424}p{margin:6px 0}</style><h2>${esc(form.title || "Event")}</h2><p>${esc(longDay(form.date))} · ${esc(form.start)} – ${esc(form.end)}</p>${form.location ? `<p>${esc(form.location)}</p>` : ""}${form.people.length ? `<p>With: ${esc(form.people.map(label).join(", "))}</p>` : ""}${form.agenda ? `<h3>Agenda</h3><p>${esc(form.agenda).replace(/\n/g, "<br>")}</p>` : ""}${form.notes ? `<p>${esc(form.notes).replace(/\n/g, "<br>")}</p>` : ""}`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  const calColor = calendars.find((c) => c.id === form.calId)?.color;

  return (
    <Dialog open modalType="modal" onOpenChange={(_, d) => !d.open && !saving && onCancel()}>
      <DialogSurface className={styles.surface} aria-label={form.id === null ? "New event" : "Edit event"}>
        <DialogBody style={{ display: "flex", flexDirection: "column", height: "100%", gap: 0, padding: 0, maxHeight: "none" }}>
          <div className={styles.head}>
            <span>{form.id === null ? "New event" : "Edit event"}</span>
            <Button appearance="subtle" icon={<Dismiss20Regular />} aria-label="Close" onClick={onCancel} disabled={saving} />
          </div>

          <div className={styles.bar} role="toolbar" aria-label="Event options">
            <Button appearance="subtle" icon={<CalendarLtr20Regular />} className={!series ? styles.barOn : undefined} aria-pressed={!series} onClick={() => { setSeries(false); onChange({ repeat: "none" }); }}>
              Event
            </Button>
            <Button appearance="subtle" icon={<ArrowRepeatAll20Regular />} className={series ? styles.barOn : undefined} aria-pressed={series} onClick={() => { setSeries(true); if (form.repeat === "none") onChange({ repeat: "weekly" }); }}>
              Series
            </Button>
            <span className={styles.barSep} />
            {calendars.length > 1 && (
              <Select aria-label="Calendar" appearance="underline" value={form.calId} onChange={(_, d) => onChange({ calId: d.value })} style={{ minWidth: 150 }}>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
            {calColor && calendars.length > 1 && <i aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 5, background: calColor, display: "inline-block" }} />}
            <span className={styles.barSep} />
            <Button appearance="subtle" icon={<Print20Regular />} aria-label="Print" onClick={print} />
            <span className={styles.grow} />
            <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <Save20Regular />} disabled={saving || !form.title.trim()} onClick={onSave} style={{ background: "#5b5fc7" }}>
              Save
            </Button>
          </div>

          {error && (
            <div className={styles.err} role="alert">
              {error}
            </div>
          )}

          <div className={styles.body}>
            <div className={styles.main}>
              <div className={styles.card}>
                <div className={styles.row}>
                  <span className={styles.titleIcon} aria-hidden="true">
                    <CalendarMonth20Regular />
                  </span>
                  <div className={`${styles.line} ${styles.titleLine}`}>
                    <input
                      className={`${styles.plain} ${styles.titleInput}`}
                      autoFocus
                      aria-label="Title"
                      placeholder="Add title"
                      value={form.title}
                      onChange={(ev) => onChange({ title: ev.target.value })}
                      onKeyDown={(ev) => ev.key === "Enter" && form.title.trim() && onSave()}
                    />
                  </div>
                </div>

                <div className={styles.row}>
                  <span className={styles.rowIcon}>
                    <People20Regular />
                  </span>
                  <div className={styles.line} onClick={() => setSugOpen(true)}>
                    {form.people.map((g) => (
                      <span key={(g.userId ?? "") + g.email + g.name} className={styles.chip}>
                        <Avatar name={label(g)} size={20} />
                        <span>{label(g)}</span>
                        <button type="button" aria-label={`Remove ${label(g)}`} onClick={() => onChange({ people: form.people.filter((p) => p !== g) })}>
                          <Dismiss20Regular style={{ width: 14, height: 14 }} />
                        </button>
                      </span>
                    ))}
                    <input
                      className={styles.plain}
                      aria-label="Invite required attendees"
                      placeholder={form.people.length ? "" : "Invite required attendees"}
                      value={typed}
                      onChange={(ev) => {
                        setTyped(ev.target.value);
                        setSugOpen(true);
                      }}
                      onFocus={() => setSugOpen(true)}
                      onBlur={() => window.setTimeout(() => { setSugOpen(false); commitTyped(); }, 150)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === "," || ev.key === ";") {
                          ev.preventDefault();
                          if (suggestions[0] && typed.trim() && ev.key === "Enter") addPerson(suggestions[0]);
                          else commitTyped();
                        } else if (ev.key === "Backspace" && !typed && form.people.length) onChange({ people: form.people.slice(0, -1) });
                      }}
                    />
                  </div>
                  {sugOpen && suggestions.length > 0 && (
                    <div className={styles.suggest} role="listbox" aria-label="People">
                      {suggestions.map((g) => (
                        <button key={(g.userId ?? "") + g.email + g.name} type="button" role="option" aria-selected="false" onMouseDown={(ev) => { ev.preventDefault(); addPerson(g); }}>
                          <Avatar name={label(g)} size={28} />
                          <span>
                            <div>{label(g)}</div>
                            {g.email && g.name && <div className={styles.muted}>{g.email}</div>}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className={styles.row}>
                  <span className={styles.rowIcon}>
                    <Clock20Regular />
                  </span>
                  <div className={`${styles.line} ${styles.when}`} style={{ borderBottomColor: clash ? "#c50f1f" : undefined }}>
                    <input type="date" aria-label="Date" value={form.date} onChange={(ev) => ev.target.value && onChange({ date: ev.target.value })} />
                    <input type="time" step={900} aria-label="Start" value={form.start} onChange={(ev) => onChange({ start: ev.target.value })} />
                    <span aria-hidden="true">–</span>
                    <input type="time" step={900} aria-label="End" value={form.end} onChange={(ev) => onChange({ end: ev.target.value })} />
                    {clash && <span className={styles.muted} style={{ color: "#c50f1f" }}>Overlaps something on your calendar</span>}
                  </div>
                </div>

                {series && (
                  <div className={styles.row}>
                    <span className={styles.rowIcon}>
                      <ArrowRepeatAll20Regular />
                    </span>
                    <Select aria-label="Repeat" appearance="underline" value={form.repeat === "none" ? "weekly" : form.repeat} onChange={(_, d) => onChange({ repeat: d.value as Repeat })} style={{ minWidth: 220 }}>
                      <option value="daily">Every day</option>
                      <option value="weekdays">Every weekday</option>
                      <option value="weekly">Every week</option>
                      <option value="monthly">Every month</option>
                      <option value="yearly">Every year</option>
                      {form.repeat === "custom" && <option value="custom">Custom repeat (unchanged)</option>}
                    </Select>
                  </div>
                )}

                <div className={styles.row}>
                  <span className={styles.rowIcon}>
                    <Location20Regular />
                  </span>
                  <div className={styles.line}>
                    <input className={styles.plain} aria-label="Location" placeholder="Add a room or location" value={form.location} onChange={(ev) => onChange({ location: ev.target.value })} />
                  </div>
                </div>

                <div className={styles.row}>
                  <span className={styles.rowIcon}>
                    <Video20Regular />
                  </span>
                  <Switch label="Spurt meeting" checked={form.meeting} onChange={(_, d) => onChange({ meeting: d.checked })} />
                </div>
                {form.meeting && (
                  <div className={styles.row} style={{ minHeight: 0, paddingBottom: 6 }}>
                    <span className={styles.rowIcon} />
                    {form.meetingUrl ? (
                      <div className={styles.linkBox} style={{ flex: 1 }}>
                        <span style={{ flex: 1 }}>{form.meetingUrl}</span>
                        <Button size="small" appearance="subtle" icon={<CopyRegular />} onClick={() => void copyLink()}>
                          Copy
                        </Button>
                      </div>
                    ) : (
                      <span className={styles.muted}>A join link is created when you save. Only the people you invite can join.</span>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.card}>
                <div className={styles.row} style={{ alignItems: "flex-start" }}>
                  <span className={styles.rowIcon} style={{ paddingTop: 14 }}>
                    <Document20Regular />
                  </span>
                  <div className={styles.editor}>
                    <textarea ref={notesRef} aria-label="Description" value={form.notes} onChange={(ev) => onChange({ notes: ev.target.value })} spellCheck />
                    <div className={styles.tools}>
                      <Button size="small" appearance="subtle" icon={<EmojiSmileSlight20Regular />} aria-label="Insert emoji" aria-expanded={emojiOpen} onClick={() => setEmojiOpen((o) => !o)} />
                      {emojiOpen && (
                        <div className={styles.emoji} role="menu">
                          {EMOJI.map((em) => (
                            <button key={em} type="button" role="menuitem" onClick={() => insertEmoji(em)}>
                              {em}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.row} style={{ alignItems: "flex-start", marginTop: 10 }}>
                  <span className={styles.rowIcon} style={{ paddingTop: 14 }}>
                    <TextBulletListLtr20Regular />
                  </span>
                  <textarea className={styles.agenda} aria-label="Agenda" placeholder="Add an agenda" rows={2} value={form.agenda} onChange={(ev) => onChange({ agenda: ev.target.value })} />
                </div>
              </div>
            </div>

            <aside className={styles.side} aria-label="Day view">
              <div className={styles.sideHead}>
                <Button appearance="subtle" size="small" icon={<ChevronLeft20Regular />} aria-label="Previous day" onClick={() => onChange({ date: addDay(form.date, -1) })} />
                <Button appearance="subtle" size="small" icon={<ChevronRight20Regular />} aria-label="Next day" onClick={() => onChange({ date: addDay(form.date, 1) })} />
                <span style={{ flex: 1 }}>{longDay(form.date)}</span>
              </div>
              <div className={styles.dayScroll} ref={scrollRef}>
                <div className={styles.dayGrid} style={{ height: ROW * 24 }}>
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className={styles.hourRow} style={{ position: "absolute", top: h * ROW, left: 0, right: 0 }} onClick={() => setSlot(h * 60)} role="button" aria-label={`Start at ${pad2(h)}:00`}>
                      <span className={styles.hourLbl}>{h}</span>
                    </div>
                  ))}
                  {busy.map((b, i) => (
                    <div key={i} className={styles.busy} style={{ top: (b.start / 60) * ROW + 1, height: Math.max(((b.end - b.start) / 60) * ROW - 2, 18) }}>
                      {b.title}
                    </div>
                  ))}
                  <div className={`${styles.slot} ${clash ? styles.slotClash : ""}`} style={{ top: (s / 60) * ROW + 1, height: Math.max(((e - s) / 60) * ROW - 2, 22) }}>
                    {form.start} – {hhmm(e)}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default NewEventDialog;
